import { useEffect, useState, type ComponentType } from 'react';
import * as Sentry from '@sentry/react';

export type BundleErrorCode =
  | 'bundle.load_failed'
  | 'bundle.server_error'
  | 'bundle.network'
  | 'bundle.invalid_mime'
  | 'sri.compute_failed'
  | 'bundle.import_failed';

export interface BundleError {
  code: BundleErrorCode;
  status?: number;
  traceId?: string;
  mime?: string;
}

export interface UseBundleLoadState {
  App?: ComponentType<unknown>;
  error?: BundleError;
  loading: boolean;
  sri?: string;
}

const ALLOWED_MIME_PREFIXES = ['application/javascript', 'text/javascript'];

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function firstNBytesAsText(buf: ArrayBuffer, n: number): string {
  const slice = new Uint8Array(buf, 0, Math.min(n, buf.byteLength));
  return new TextDecoder('utf-8', { fatal: false }).decode(slice);
}

/**
 * Fetcha bundle, valida MIME + integridade via SHA-384, importa via blob URL.
 *
 * - SRI é runtime (compute + log; sem persist — ver docs/04-bundle-pipeline.md + ADR-001).
 * - `retryNonce` bump força re-fetch sem mudar URL (click "Tentar de novo").
 * - URL.revokeObjectURL chamado em finally (leak prevention).
 * - Fix #20: MIME guard rejeita resposta 200 com body não-JS (origin error,
 *   captive portal, edge worker error page); Sentry capture em invalid_mime
 *   + import_failed surface o eventId como `traceId` pra UI mostrar pro user.
 */
export function useBundleLoad(
  slug: string,
  bundleUrl: string,
  retryNonce: number,
  appId: string,
): UseBundleLoadState {
  const [state, setState] = useState<UseBundleLoadState>({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    let blobUrl: string | undefined;

    (async () => {
      let buf: ArrayBuffer;
      let mime: string;
      try {
        const res = await fetch(bundleUrl);
        if (!res.ok) {
          if (cancelled) return;
          setState({
            loading: false,
            error: {
              code: res.status >= 500 ? 'bundle.server_error' : 'bundle.load_failed',
              status: res.status,
            },
          });
          return;
        }
        mime = (res.headers.get('content-type') ?? '').toLowerCase();
        if (!ALLOWED_MIME_PREFIXES.some((prefix) => mime.startsWith(prefix))) {
          buf = await res.arrayBuffer();
          if (cancelled) return;
          const traceId = Sentry.captureException(
            new Error(`bundle.invalid_mime: got "${mime}"`),
            {
              tags: {
                code: 'bundle.invalid_mime',
                app_id: appId,
                bundle_url: bundleUrl,
                mime,
              },
              extra: {
                first_200_bytes: firstNBytesAsText(buf, 200),
              },
            },
          );
          setState({
            loading: false,
            error: { code: 'bundle.invalid_mime', mime, traceId: String(traceId) },
          });
          return;
        }
        buf = await res.arrayBuffer();
      } catch {
        if (!cancelled) setState({ loading: false, error: { code: 'bundle.network' } });
        return;
      }

      let sri: string;
      try {
        const digest = await crypto.subtle.digest('SHA-384', buf);
        sri = 'sha384-' + bytesToBase64(new Uint8Array(digest));
      } catch {
        if (!cancelled) setState({ loading: false, error: { code: 'sri.compute_failed' } });
        return;
      }

      console.debug(`[AppLoader] bundle SRI (${slug}): ${sri}, bytes=${buf.byteLength}`);

      try {
        blobUrl = URL.createObjectURL(new Blob([buf], { type: 'application/javascript' }));
        const mod = await import(/* @vite-ignore */ blobUrl);
        if (cancelled) return;
        const App = (mod as { default?: ComponentType<unknown> }).default;
        if (typeof App !== 'function') {
          console.error('[AppLoader] bundle has no default function export', mod);
          const traceId = Sentry.captureException(
            new Error('bundle.import_failed: missing default export'),
            {
              tags: {
                code: 'bundle.import_failed',
                app_id: appId,
                bundle_url: bundleUrl,
                sri_computed: sri,
              },
              extra: { first_200_bytes: firstNBytesAsText(buf, 200) },
            },
          );
          setState({
            loading: false,
            error: { code: 'bundle.import_failed', traceId: String(traceId) },
            sri,
          });
          return;
        }
        setState({ loading: false, App, sri });
      } catch (e) {
        console.error('[AppLoader] import failed:', e);
        if (cancelled) return;
        const traceId = Sentry.captureException(
          e instanceof Error ? e : new Error(String(e)),
          {
            tags: {
              code: 'bundle.import_failed',
              app_id: appId,
              bundle_url: bundleUrl,
              sri_computed: sri,
            },
            extra: { first_200_bytes: firstNBytesAsText(buf, 200) },
          },
        );
        setState({
          loading: false,
          error: { code: 'bundle.import_failed', traceId: String(traceId) },
          sri,
        });
      } finally {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, bundleUrl, retryNonce, appId]);

  return state;
}
